import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import {
  invitationService,
  type TeamMemberOrInvitation,
} from '@/services/invitations'
import { useAuthStore } from '@/stores/auth'

// Dynamic query key that includes organization ID
const getQueryKey = (organizationId?: string) =>
  ['organization', organizationId, 'team-members'] as const

/**
 * Hook for managing team members and invitations
 * Provides operations for fetching, inviting, and removing team members
 */
export function useTeamMembers() {
  const queryClient = useQueryClient()
  const { role, organization } = useAuthStore()

  // Check if current user has God Mode (Super Admin) permissions
  const hasGodModePermissions = role?.name === 'Super Admin'

  // Query for fetching team members and invitations
  const queryKey = getQueryKey(organization?.id)
  const {
    data: teamMembers = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => invitationService.getTeamMembersAndInvitations(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!organization?.id,
  })

  // Invite user mutation
  const inviteUserMutation = useMutation({
    mutationFn: (email: string) => invitationService.inviteUser(email),
    onSuccess: () => {
      // Refetch team members to include new invitation
      refetch()
    },
  })

  // Remove user mutation (God Mode only)
  const removeUserMutation = useMutation({
    mutationFn: (userId: string) => invitationService.removeUser(userId),
    onSuccess: (_, removedUserId) => {
      // Optimistically update the cache
      queryClient.setQueryData(
        queryKey,
        (oldMembers: TeamMemberOrInvitation[] = []) =>
          oldMembers.filter((member) =>
            member.type === 'user' ? member.id !== removedUserId : true
          )
      )
    },
  })

  // Revoke invitation mutation
  const revokeInvitationMutation = useMutation({
    mutationFn: (invitationId: string) =>
      invitationService.revokeInvitation(invitationId),
    onSuccess: (_, revokedInvitationId) => {
      // Optimistically update the cache
      queryClient.setQueryData(
        queryKey,
        (oldMembers: TeamMemberOrInvitation[] = []) =>
          oldMembers.filter((member) =>
            member.type === 'invitation'
              ? member.id !== revokedInvitationId
              : true
          )
      )
    },
  })

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: (invitationId: string) =>
      invitationService.resendInvitation(invitationId),
    onSuccess: (newInvitationData, oldInvitationId) => {
      // Replace the old invitation with the new one in the cache
      queryClient.setQueryData(
        queryKey,
        (oldMembers: TeamMemberOrInvitation[] = []) =>
          oldMembers.map((member) =>
            member.type === 'invitation' && member.id === oldInvitationId
              ? { ...newInvitationData, type: 'invitation' as const }
              : member
          )
      )
    },
  })

  // Filter users and invitations
  const users = teamMembers.filter((member) => member.type === 'user')
  const invitations = teamMembers.filter(
    (member) => member.type === 'invitation'
  )

  // Actions
  const inviteUser = useCallback(
    (email: string) => {
      return inviteUserMutation.mutateAsync(email)
    },
    [inviteUserMutation]
  )

  const removeUser = useCallback(
    (userId: string) => {
      if (!hasGodModePermissions) {
        throw new Error('Insufficient permissions to remove users')
      }
      return removeUserMutation.mutateAsync(userId)
    },
    [removeUserMutation, hasGodModePermissions]
  )

  const revokeInvitation = useCallback(
    (invitationId: string) => {
      return revokeInvitationMutation.mutateAsync(invitationId)
    },
    [revokeInvitationMutation]
  )

  const resendInvitation = useCallback(
    (invitationId: string) => {
      return resendInvitationMutation.mutateAsync(invitationId)
    },
    [resendInvitationMutation]
  )

  return {
    // Data
    teamMembers,
    users,
    invitations,

    // Permissions
    hasGodModePermissions,

    // Loading states
    isLoading,
    isInviting: inviteUserMutation.isPending,
    isRemoving: removeUserMutation.isPending,
    isRevoking: revokeInvitationMutation.isPending,
    isResending: resendInvitationMutation.isPending,

    // Errors
    error,
    inviteError: inviteUserMutation.error,
    removeError: removeUserMutation.error,
    revokeError: revokeInvitationMutation.error,
    resendError: resendInvitationMutation.error,

    // Actions
    inviteUser,
    removeUser,
    revokeInvitation,
    resendInvitation,
    refetch,

    // Mutation objects for advanced usage
    inviteUserMutation,
    removeUserMutation,
    revokeInvitationMutation,
    resendInvitationMutation,
  }
}
